import * as React from "react";
import { css } from "@emotion/core";
import {
  Card,
  Button,
  Tag,
  Tooltip,
  Icon,
  UL,
  MenuItem,
  Spinner,
  NonIdealState,
} from "@blueprintjs/core";
import { Select, ItemRenderer } from "@blueprintjs/select";
import { OrderMachineContext } from "../state/orderMachine";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { useSinkState } from "context/sink";
import ProcessProgress from "./ProcessProgress";
import ApiOrders from "api/orders";
import { useFolderOrderTypes } from "hooks";
import { IOrderType } from "api/admin";

const OrderTypeSelectComponent = Select.ofType<IOrderType>();

const renderOrderType: ItemRenderer<IOrderType> = (
  orderType,
  { handleClick, handleFocus, modifiers }
) => {
  if (!modifiers.matchesPredicate) {
    return null;
  }

  return (
    <MenuItem
      active={modifiers.active}
      disabled={modifiers.disabled}
      key={orderType.id}
      onClick={handleClick}
      onFocus={handleFocus}
      text={orderType.name}
      label={orderType.site_label ? `${orderType.site_label}` : undefined}
      icon={modifiers.active ? "tick" : "blank"}
    />
  );
};

const filterOrderType = (query: string, orderType: IOrderType) => {
  return (
    orderType.name.toLowerCase().indexOf(query.toLowerCase()) >= 0 ||
    (orderType.description &&
      orderType.description.toLowerCase().indexOf(query.toLowerCase()) >= 0) ||
    orderType.id.toString().indexOf(query) >= 0
  );
};

export function OrderTypeSelect({
  orderTypes,
  selectedOrderType,
  onOrderTypeSelect,
  disabled,
}: {
  orderTypes: IOrderType[];
  selectedOrderType: number | null;
  onOrderTypeSelect: (orderType: IOrderType) => void;
  disabled?: boolean;
}) {
  return (
    <OrderTypeSelectComponent
      items={orderTypes}
      activeItem={orderTypes.find(
        (orderType) => orderType.id === selectedOrderType
      )}
      itemRenderer={renderOrderType}
      itemPredicate={filterOrderType}
      onItemSelect={onOrderTypeSelect}
      filterable={false}
      resetOnSelect={false}
      resetOnClose={false}
      disabled={disabled}
    >
      <Button
        size="small"
        text={
          selectedOrderType
            ? orderTypes.find((orderType) => orderType.id === selectedOrderType)
                ?.name
            : "Select a product type"
        }
        endIcon="caret-down"
        intent="primary"
        disabled={disabled}
      />
    </OrderTypeSelectComponent>
  );
}

function OrderTypeCard({
  title,
  description,
  items,
  siteLabel,
  onClick,
}: {
  title: string;
  description: string;
  items?: string[];
  siteLabel?: string;
  onClick: () => void;
}) {
  const [expanded, setExpanded] = React.useState(false);

  const itemCount = items?.length ?? 0;

  return (
    <Card
      interactive
      css={css`
        padding: 1rem;

        & > div {
          color: var(--neutral-5);
        }

        &:hover > div {
          color: var(--neutral-3);
        }
      `}
      onClick={(e) => {
        setExpanded((prev) => !prev);
      }}
    >
      <div
        css={css`
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 1rem;
        `}
      >
        <Icon icon={expanded ? "collapse-all" : "expand-all"} />
        <div
          css={css`
            flex: 1;
          `}
        >
          <div
            css={css`
              display: flex;
              gap: 0.5rem;
              justify-content: space-between;
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 0.25rem;
              `}
            >
              <h2
                css={css`
                  font-size: 1.1rem;
                  color: var(--neutral-1);
                  font-weight: 700;
                `}
              >
                {title}
              </h2>
              {siteLabel ? (
                <h3
                  css={css`
                    font-size: 1rem;
                    color: var(--neutral-3);
                    font-weight: 600;
                  `}
                >
                  Site: {siteLabel}
                </h3>
              ) : null}
            </div>
            <div
              css={css`
                flex-shrink: 0;
              `}
            >
              <Button
                intent="primary"
                endIcon="arrow-right"
                onClick={(e) => {
                  e.stopPropagation();
                  onClick();
                }}
              >
                Choose
              </Button>
            </div>
          </div>
          <p
            css={css`
              font-size: 0.9rem;
              color: var(--neutral-4);
              font-weight: 400;
              margin: 0.5rem 0;
              padding-right: 2rem;
            `}
          >
            <span
              css={css`
                display: -webkit-box;
                -webkit-box-orient: vertical;
                overflow: hidden;
                ${expanded ? "" : "-webkit-line-clamp: 2;"}
              `}
            >
              {description}
            </span>
          </p>
          {itemCount > 0 ? (
            <div
              css={css`
                padding: 0.5rem 0;
              `}
            >
              <Tooltip
                placement="bottom"
                content={
                  <div
                    css={css`
                      max-width: 60ch;
                      padding: 0 1rem 0 0;
                    `}
                  >
                    <UL>
                      {(items ?? []).map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </UL>
                  </div>
                }
              >
                <Tag
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {itemCount} Available Output
                  {itemCount > 1 ? "s" : ""}
                </Tag>
              </Tooltip>
            </div>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

const ChooseOrderTypeContent = (props: { children: React.ReactNode }) => {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        width: 100%;
      `}
    >
      {props.children}
    </div>
  );
};

export default function ChooseOrderType() {
  const { send } = OrderMachineContext.useActorRef();
  const navigate = useNavigate();

  const { surveys, selectedSurveyId, sink } = useSinkState();
  const survey = surveys.find((survey) => survey.id === selectedSurveyId);
  const folderId = survey?.folder_id;

  const existingOrder = sink.orders.find(
    (order) =>
      order.survey_id === selectedSurveyId &&
      order.submitted === null &&
      order.organization_order_type_id !== undefined // Only consider new-style orders
  );

  const {
    data: orderTypes,
    isLoading,
    error,
  } = useFolderOrderTypes(folderId);

  if (!folderId) {
    return (
      <ChooseOrderTypeContent>
        <ProcessProgress size="small" current={1} />
        <NonIdealState
          icon="folder-open"
          title="No Folder Assigned"
          description="This survey does not have a folder assigned. Please assign a folder to create an order."
        />
        <Outlet />
      </ChooseOrderTypeContent>
    );
  }

  if (isLoading) {
    return (
      <ChooseOrderTypeContent>
        <ProcessProgress size="small" current={1} />
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100%;
          `}
        >
          <Spinner />
        </div>
        <Outlet />
      </ChooseOrderTypeContent>
    );
  }

  if (error) {
    return (
      <>
        <ProcessProgress size="small" current={1} />
        <NonIdealState
          icon="error"
          title="Error Loading Order Types"
          description={
            error instanceof Error
              ? error.message
              : "Failed to load order types"
          }
        />
        <Outlet />
      </>
    );
  }

  if (!orderTypes || orderTypes.length === 0) {
    return (
      <ChooseOrderTypeContent>
        <ProcessProgress size="small" current={1} />
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1rem;
          `}
        >
          <NonIdealState
            icon="inbox"
            title="No Order Types Available"
            description={
              <>
                This folder has no order types assigned. To have order types
                assigned please contact an administrator within your
                organization,{" "}
                <a href="mailto:support@fybr.ca">support@fybr.ca</a> or{" "}
                <a href="mailto:sales@fybr.ca">sales@fybr.ca</a>.
              </>
            }
          />
        </div>
        <Outlet />
      </ChooseOrderTypeContent>
    );
  }

  return (
    <ChooseOrderTypeContent>
      <ProcessProgress size="small" current={1} />
      <div
        css={css`
          display: flex;
          flex-direction: column;
          overflow-y: auto;
          width: 100%;
          border-top: 1px solid var(--neutral-6);
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 1rem;
            max-width: 768px;
            min-width: 512px;
            margin: 0 auto;
            padding: 2rem 0;
          `}
        >
          {orderTypes.map((orderType) => (
            <OrderTypeCard
              key={orderType.id}
              title={orderType.name}
              description={orderType.description || ""}
              items={orderType.items}
              siteLabel={orderType.site_label || undefined}
              onClick={async () => {
                if (existingOrder) {
                  navigate(`/surveys/${survey?.id}/orders/${existingOrder.id}`);
                } else {
                  try {
                    send({ type: "SELECT_TYPE", orderTypeId: orderType.id });
                  } catch (error) {
                    console.error("Failed to create order:", error);
                  }
                }
              }}
            />
          ))}
        </div>
      </div>
      <Outlet />
    </ChooseOrderTypeContent>
  );
}
